'use client';

import React from 'react';
import Link from 'next/link';
import ImageWrapper from '@/helpers/Wrappers/ImageWrapper/ImageWrapper';
import { toEnhancedImage } from '@/utils/image-utils';
import { useParams } from 'next/navigation';
import { IStandardContentPage } from '@/.generated';
import PlainTextWrapper from '@/helpers/Wrappers/PlainTextWrapper/PlainTextWrapper';
import { getCSLPAttributes } from '@/utils/type-guards';
import { usePageReferenceUrl } from '@/lib/hooks/usePageReferenceUrl';
import { trackAdobeClick } from '@/utils/adobe/click';
import { useGlobalLabels } from '@/context/GlobalLabelContext';
import { setLanguagePreference } from '@/app/actions/language';
import { DEFAULT_LOCALE } from '@/constants/locales';

const ES_LANG_CODE = 'es';

/** A single CMS-authored link entry in Column 2. */
export type Col2Link = NonNullable<
  NonNullable<NonNullable<IStandardContentPage['in_page_navigation']>['column_2']>['column_links']
>[number];

interface Col2LinkItemProps {
  item: Col2Link;
  className: string;
}

// Shared <li><Link> markup rendered once the href is known.
const LinkListItem = ({ href, item, className }: Col2LinkItemProps & { href: string }) => {
  const { globalLabels } = useGlobalLabels();
  const params = useParams();
  const currentLocale = (params?.locale as string | undefined) ?? DEFAULT_LOCALE;

  const engOnlyLink = item.english_only_link;
  const engOnlyIcon = globalLabels.english_only_identifier_icon;

  // When the current session is Spanish and the link is flagged as English-only,
  // switch the language preference to English before navigating so the target
  // page renders in English rather than attempting a Spanish variant.
  const shouldSwitchToEnglish = engOnlyLink && currentLocale === ES_LANG_CODE;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    trackAdobeClick(
      {
        clickName: item.link?.title || '',
        clickLocation: 'InPageNavigation',
        clickType: 'inpagenavigation-link',
      },
      e
    );
    if (!shouldSwitchToEnglish) return;
    e.preventDefault();
    void setLanguagePreference(DEFAULT_LOCALE, href);
  };

  return (
    <li>
      <Link
        href={href}
        className={className}
        {...getCSLPAttributes(item.$?.link)}
        onClick={handleClick}
      >
        <PlainTextWrapper content={item.link?.title} isDangerouslySetInnerHTML />
        {engOnlyLink && engOnlyIcon?.url && (
          <span aria-hidden="true" className="ms-2.5 w-fit inline-block">
            <ImageWrapper
              image={toEnhancedImage(engOnlyIcon, {
                alternateText: engOnlyIcon.title,
                fallbackWidth: 16,
                fallbackHeight: 16,
              })}
              wrapperClassName="w-fit h-fit"
              imageClassName="h-[0.8rem] w-auto"
            />
          </span>
        )}
      </Link>
    </li>
  );
};

// Resolves a page reference to its localized URL via the CMS before rendering.
const ReferencedLinkItem = ({ item, className }: Col2LinkItemProps) => {
  const href = usePageReferenceUrl(item.page_reference, item.link?.href);
  if (!href) return null;
  return <LinkListItem href={href} item={item} className={className} />;
};

// Renders a single Column 2 link; skips the CMS-resolving hook entirely when
// no page reference is authored, so no CMS API call is made for plain links.
export const Col2LinkItem = ({ item, className }: Col2LinkItemProps) => {
  if (!item.page_reference?.length) {
    const href = item.link?.href;
    return href ? <LinkListItem href={href} item={item} className={className} /> : null;
  }
  return <ReferencedLinkItem item={item} className={className} />;
};
